// src/app/activities/page.tsx - Converted to use centralized theming system
'use client';

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  Tab,
  Tabs,
  Avatar,
  Skeleton,
  Pagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CalendarToday,
  LocationOn,
  Person,
  Search,
  FilterList,
  OpenInNew,
  WorkOutline as InternshipIcon,
  School as WorkshopIcon,
  Business as ProfDevIcon,
  Science as TrainingIcon,
  Event as SeminarIcon,
  Group as GroupIcon,
  AccessTime,
  ArrowForward,
  Business as BusinessIcon,
  Help as QuestionIcon,
} from '@mui/icons-material';
import Image from 'next/image';

// Import centralized types and hooks
import type { 
  Activity,
  ActivityType,
  Person,
  Organization,
  ResearchTheme,
} from '@/types';
import { useActivities } from '@/hooks/useApi';
import { getMediaUrl, formatDate } from '@/lib/api';

// Import our modular color system
import { getActiveColorTheme } from '@/lib/colors';

// =====================
// STYLED COMPONENTS
// =====================

const HeroSection = styled(Box)(() => {
  const colors = getActiveColorTheme();
  return {
    minHeight: '40vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    background: colors.background.hero,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("/api/placeholder/1920/600") center/cover',
      opacity: 0.2,
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: colors.effects.overlay,
      zIndex: 2,
    }
  };
});

const HeroContent = styled(Box)(() => {
  const colors = getActiveColorTheme();
  return {
    position: 'relative',
    zIndex: 3,
    color: colors.text.primary,
    textAlign: 'center',
  };
});

const FilterContainer = styled(Paper)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.secondary}`,
    padding: '24px',
    marginBottom: '32px',
  };
});

const ActivityCard = styled(Card)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: '16px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-4px)',
      boxShadow: `0 12px 40px ${colors.effects.shadow}`,
    },
  };
});

const ActivityTypeChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'activityType',
})<{ activityType: ActivityType }>(({ activityType }) => {
  const colors = getActiveColorTheme();
  
  const getColors = (type: ActivityType) => {
    switch (type) {
      case 'Internship': return { bg: 'rgba(255, 87, 34, 0.2)', color: '#ff7043', border: '#ff7043' };
      case 'Workshop': return { bg: 'rgba(63, 81, 181, 0.2)', color: '#5c6bc0', border: '#5c6bc0' };
      case 'Professional Development': return { bg: 'rgba(0, 150, 136, 0.2)', color: '#26a69a', border: '#26a69a' };
      case 'Training': return { bg: 'rgba(233, 30, 99, 0.2)', color: '#ec407a', border: '#ec407a' };
      case 'Seminar': return { bg: 'rgba(121, 85, 72, 0.2)', color: '#8d6e63', border: '#8d6e63' };
      case 'Summer Institute': return { bg: colors.effects.glow, color: colors.brand.accent.main, border: colors.brand.accent.main };
      default: return { bg: 'rgba(158, 158, 158, 0.2)', color: '#bdbdbd', border: '#bdbdbd' };
    }
  };
  
  const typeColors = getColors(activityType);
  
  return {
    background: typeColors.bg,
    color: typeColors.color,
    border: `1px solid ${typeColors.border}`,
    fontSize: '0.75rem',
    fontWeight: 500,
  };
});

const StatsContainer = styled(Box)(() => ({
  display: 'flex',
  gap: '24px',
  marginBottom: '32px',
  justifyContent: 'center',
  flexWrap: 'wrap',
}));

const StatCard = styled(Paper)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.primary}`,
    padding: '16px',
    textAlign: 'center',
    minWidth: '120px',
    
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-2px)',
      transition: 'all 0.3s ease',
    },
  };
});

const ParticipantAvatar = styled(Avatar)(() => {
  const colors = getActiveColorTheme();
  return {
    width: 32,
    height: 32,
    marginRight: '4px',
    border: `2px solid ${colors.effects.glow}`,
    fontSize: '0.8rem',
    
    '&:hover': {
      borderColor: colors.brand.accent.main,
    },
  };
});

const ThemeChip = styled(Chip)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.effects.glow,
    color: colors.text.secondary,
    border: `1px solid ${colors.effects.glow}`,
    margin: '2px',
    fontSize: '0.7rem',
    height: '24px',
    
    '&:hover': {
      background: colors.effects.glow,
      borderColor: colors.brand.accent.main,
    },
  };
});

const ThemedTextField = styled(TextField)(() => {
  const colors = getActiveColorTheme();
  return {
    '& .MuiOutlinedInput-root': {
      color: colors.text.secondary,
      '& fieldset': {
        borderColor: colors.border.primary,
      },
      '&:hover fieldset': {
        borderColor: colors.border.accent,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.border.accent,
      },
    },
  };
});

const ThemedSelect = styled(Select)(() => {
  const colors = getActiveColorTheme();
  return {
    color: colors.text.secondary,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.border.primary,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.border.accent,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.border.accent,
    },
  };
});

const ThemedPagination = styled(Pagination)(() => {
  const colors = getActiveColorTheme();
  return {
    '& .MuiPaginationItem-root': {
      color: colors.text.secondary,
      borderColor: colors.border.primary,
      '&:hover': {
        backgroundColor: colors.effects.glow,
        borderColor: colors.border.accent,
      },
      '&.Mui-selected': {
        backgroundColor: colors.brand.accent.main,
        color: colors.brand.accent.contrastText,
        '&:hover': {
          backgroundColor: colors.brand.accent.dark,
        },
      },
    },
  };
});

const PrimaryButton = styled(Button)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.primary,
    color: colors.brand.accent.contrastText,
    fontWeight: 600,
    '&:hover': {
      background: colors.gradients.primary,
      filter: 'brightness(1.1)',
    }
  };
});

const SecondaryButton = styled(Button)(() => {
  const colors = getActiveColorTheme();
  return {
    color: colors.brand.accent.main,
    borderColor: colors.border.accent,
    '&:hover': {
      background: colors.effects.glow,
      borderColor: colors.border.accent,
    }
  };
});

// =====================
// SUB-COMPONENTS
// =====================

interface ActivityItemProps {
  activity: Activity;
  onClick: (activity: Activity) => void;
}

const ActivityItem = React.memo<ActivityItemProps>(({ activity, onClick }) => {
  const colors = getActiveColorTheme();
  
  const handleClick = useCallback(() => {
    onClick(activity);
  }, [activity, onClick]);

  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case 'Internship': return <InternshipIcon />;
      case 'Workshop': return <WorkshopIcon />;
      case 'Professional Development': return <ProfDevIcon />;
      case 'Training': return <TrainingIcon />;
      case 'Seminar': return <SeminarIcon />;
      case 'Summer Institute': return <WorkshopIcon />;
      default: return <WorkshopIcon />;
    }
  };

  const isUpcoming = activity.startDate ? new Date(activity.startDate) > new Date() : false;
  const hasApplicationDeadline = activity.applicationDeadline && new Date(activity.applicationDeadline) > new Date();

  return (
    <ActivityCard onClick={handleClick}>
      <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header with type and status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <ActivityTypeChip
            icon={getActivityIcon(activity.activityType)}
            label={activity.activityType}
            size="small"
            activityType={activity.activityType}
          />
          
          {hasApplicationDeadline && (
            <Chip
              label="Open for Applications"
              size="small"
              sx={{
                background: 'rgba(76, 175, 80, 0.2)',
                color: '#66bb6a',
                border: '1px solid #66bb6a',
                fontSize: '0.7rem',
              }}
            />
          )}
        </Box>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            color: colors.brand.accent.main,
            fontWeight: 600,
            marginBottom: 1,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {activity.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: colors.text.secondary,
            lineHeight: 1.6,
            marginBottom: 2,
            flexGrow: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {activity.description}
        </Typography>

        {/* Date and Location Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {activity.startDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: '0.9rem', color: colors.text.disabled }} />
              <Typography variant="caption" sx={{ color: colors.text.disabled }}>
                {formatDate(activity.startDate)}
                {activity.endDate && ` - ${formatDate(activity.endDate)}`}
              </Typography>
            </Box>
          )}
          
          {activity.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOn sx={{ fontSize: '0.9rem', color: colors.text.disabled }} />
              <Typography variant="caption" sx={{ color: colors.text.disabled }}>
                {activity.location}
              </Typography>
            </Box>
          )}

          {activity.applicationDeadline && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: '0.9rem', color: hasApplicationDeadline ? '#66bb6a' : colors.text.disabled }} />
              <Typography variant="caption" sx={{ color: hasApplicationDeadline ? '#66bb6a' : colors.text.disabled }}>
                Application Deadline: {formatDate(activity.applicationDeadline)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Organizers */}
        {activity.organizers && activity.organizers.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
            <Person sx={{ fontSize: '0.9rem', color: colors.text.disabled }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {activity.organizers.slice(0, 3).map((organizer) => (
                <ParticipantAvatar key={organizer.id}>
                  {organizer.name?.charAt(0) || '?'}
                </ParticipantAvatar>
              ))}
              {activity.organizers.length > 3 && (
                <Typography variant="caption" sx={{ color: colors.text.disabled, alignSelf: 'center' }}>
                  +{activity.organizers.length - 3} more
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Related Themes */}
        {activity.relatedThemes && activity.relatedThemes.length > 0 && (
          <Box sx={{ mt: 'auto' }}>
            <Typography variant="caption" sx={{ color: colors.text.disabled, display: 'block', mb: 0.5 }}>
              Related Themes:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {activity.relatedThemes.slice(0, 3).map((theme) => (
                <ThemeChip
                  key={theme.id}
                  label={theme.themeNumber}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Action Buttons */}
        {(activity.applicationLink || hasApplicationDeadline) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.effects.glow}` }}>
            {activity.applicationLink && hasApplicationDeadline && (
              <PrimaryButton
                variant="contained"
                size="small"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(activity.applicationLink, '_blank', 'noopener,noreferrer');
                }}
                sx={{ fontSize: '0.8rem' }}
              >
                Apply Now
              </PrimaryButton>
            )}
          </Box>
        )}
      </CardContent>
    </ActivityCard>
  );
});

ActivityItem.displayName = 'ActivityItem';

const LoadingSkeleton = React.memo(() => {
  const colors = getActiveColorTheme();
  
  return (
    <>
      {Array.from({ length: 6 }, (_, index) => (
        <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ 
            background: colors.gradients.card,
            border: `1px solid ${colors.border.primary}`,
            height: 400,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Skeleton 
                variant="rectangular" 
                height={32} 
                width="40%" 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} 
              />
              <Skeleton 
                variant="text" 
                height={28} 
                width="80%" 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.15)', mb: 1 }} 
              />
              <Skeleton 
                variant="text" 
                height={16} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 1 }} 
              />
              <Skeleton 
                variant="text" 
                height={16} 
                width="90%" 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} 
              />
              <Skeleton 
                variant="text" 
                height={16} 
                width="70%" 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
              />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </>
  );
});

LoadingSkeleton.displayName = 'LoadingSkeleton';

// =====================
// MAIN COMPONENT
// =====================

const ActivitiesPage: React.FC = () => {
  const colors = getActiveColorTheme();
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<ActivityType | ''>('');
  const [showOnlyOpen, setShowOnlyOpen] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // API data
  const { activities: allActivities, loading, error } = useActivities();

  // Filter and pagination logic
  const filteredActivities = useMemo(() => {
    return allActivities.filter(activity => {
      const matchesSearch = searchQuery === '' || 
        activity.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        activity.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesType = selectedType === '' || activity.activityType === selectedType;
      
      const matchesOpenStatus = !showOnlyOpen || 
        (activity.applicationDeadline && new Date(activity.applicationDeadline) > new Date());
      
      return matchesSearch && matchesType && matchesOpenStatus;
    });
  }, [allActivities, searchQuery, selectedType, showOnlyOpen]);

  const { upcomingActivities, pastActivities } = useMemo(() => {
    const now = new Date();
    const upcoming = filteredActivities.filter(activity => 
      !activity.startDate || new Date(activity.startDate) > now
    );
    const past = filteredActivities.filter(activity => 
      activity.startDate && new Date(activity.startDate) <= now
    );
    
    return {
      upcomingActivities: upcoming.sort((a, b) => {
        if (!a.startDate) return 1;
        if (!b.startDate) return -1;
        return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
      }),
      pastActivities: past.sort((a, b) => {
        if (!a.startDate) return 1;
        if (!b.startDate) return -1;
        return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
      })
    };
  }, [filteredActivities]);

  // Paginated data
  const paginatedActivities = useMemo(() => {
    const allSorted = [...upcomingActivities, ...pastActivities];
    const startIndex = (currentPage - 1) * itemsPerPage;
    return allSorted.slice(startIndex, startIndex + itemsPerPage);
  }, [upcomingActivities, pastActivities, currentPage]);

  const totalPages = Math.ceil((upcomingActivities.length + pastActivities.length) / itemsPerPage);

  // Event handlers
  const handleActivityClick = useCallback((activity: Activity) => {
    console.log('Navigate to activity:', activity.slug || activity.id);
    // TODO: Implement navigation to activity detail page
    // router.push(`/activities/${activity.slug || activity.id}`);
  }, []);

  const openApplicationsCount = useMemo(() => {
    return allActivities.filter(activity => 
      activity.applicationDeadline && new Date(activity.applicationDeadline) > new Date()
    ).length;
  }, [allActivities]);

  return (
    <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <HeroContent>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                marginBottom: 2,
                background: colors.gradients.secondary,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Program Activities
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                fontWeight: 400,
                color: colors.text.secondary,
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.5,
              }}
            >
              Hands-on training, professional development, and collaborative learning opportunities
            </Typography>
          </HeroContent>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Statistics */}
        <StatsContainer>
          <StatCard>
            <WorkshopIcon sx={{ color: colors.brand.accent.main, fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.main, fontWeight: 700 }}>
              {allActivities.length}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              Total Activities
            </Typography>
          </StatCard>
          
          <StatCard>
            <AccessTime sx={{ color: colors.brand.accent.main, fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.main, fontWeight: 700 }}>
              {upcomingActivities.length}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              Upcoming
            </Typography>
          </StatCard>
          
          <StatCard>
            <OpenInNew sx={{ color: colors.brand.accent.main, fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.main, fontWeight: 700 }}>
              {openApplicationsCount}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              Open Applications
            </Typography>
          </StatCard>
        </StatsContainer>

        {/* Filters */}
        <FilterContainer>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <ThemedTextField
                fullWidth
                placeholder="Search activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: colors.brand.accent.main }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: colors.text.disabled }}>Activity Type</InputLabel>
                <ThemedSelect
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value as ActivityType | '')}
                  label="Activity Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="Summer Institute">Summer Institute</MenuItem>
                  <MenuItem value="Workshop">Workshops</MenuItem>
                  <MenuItem value="Internship">Internships</MenuItem>
                  <MenuItem value="Professional Development">Professional Development</MenuItem>
                  <MenuItem value="Training">Training</MenuItem>
                  <MenuItem value="Seminar">Seminars</MenuItem>
                </ThemedSelect>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant={showOnlyOpen ? "contained" : "outlined"}
                onClick={() => setShowOnlyOpen(!showOnlyOpen)}
                sx={{
                  color: showOnlyOpen ? colors.brand.accent.contrastText : colors.brand.accent.main,
                  borderColor: colors.border.accent,
                  background: showOnlyOpen ? colors.gradients.primary : 'transparent',
                  py: 1.5,
                  '&:hover': {
                    background: showOnlyOpen 
                      ? colors.gradients.primary
                      : colors.effects.glow,
                    borderColor: colors.border.accent,
                    filter: showOnlyOpen ? 'brightness(1.1)' : 'none',
                  }
                }}
              >
                Open Only
              </Button>
            </Grid>
            
            <Grid size={{ xs: 12, md: 3 }}>
              <SecondaryButton
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchQuery('');
                  setSelectedType('');
                  setShowOnlyOpen(false);
                  setCurrentPage(1);
                }}
                sx={{ py: 1.5 }}
              >
                Clear Filters
              </SecondaryButton>
            </Grid>
          </Grid>
        </FilterContainer>

        {/* Content */}
        {loading ? (
          <Grid container spacing={3}>
            <LoadingSkeleton />
          </Grid>
        ) : error ? (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              background: colors.gradients.card,
              border: `1px solid ${colors.border.primary}`,
            }}
          >
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 2 }}>
              Unable to Load Activities
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              {error}
            </Typography>
          </Paper>
        ) : paginatedActivities.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {paginatedActivities.map((activity) => (
                <Grid key={activity.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <ActivityItem activity={activity} onClick={handleActivityClick} />
                </Grid>
              ))}
            </Grid>
            
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <ThemedPagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, page) => setCurrentPage(page)}
                />
              </Box>
            )}
          </>
        ) : (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              background: colors.gradients.card,
              border: `1px solid ${colors.border.primary}`,
            }}
          >
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
              No Activities Found
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              {searchQuery || selectedType || showOnlyOpen
                ? 'Try adjusting your search criteria or clearing filters.'
                : 'Activities will appear here once they are scheduled.'}
            </Typography>
          </Paper>
        )}

        {/* Call to Action */}
        {allActivities.length > 0 && (
          <Paper
            sx={{
              p: 4,
              mt: 6,
              textAlign: 'center',
              background: colors.gradients.card,
              border: `1px solid ${colors.border.accent}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: colors.brand.accent.main,
                fontWeight: 600,
                marginBottom: 2,
              }}
            >
              Ready to Get Involved?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.text.secondary,
                marginBottom: 3,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Explore our program activities and find opportunities that match your interests and career goals. 
              From hands-on workshops to industry internships, we offer diverse pathways for professional development.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <PrimaryButton
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  px: 4,
                  py: 1.5,
                }}
              >
                Contact Us
              </PrimaryButton>
              
              <SecondaryButton
                variant="outlined"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                }}
              >
                Learn More
              </SecondaryButton>
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default ActivitiesPage;